import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown as ChevronDownIcon } from "lucide-react";
import Label from "components/crm/ui/Label";
import useCloseOnEscape from "components/crm/ui/useCloseOnEscape";
import useText from "hooks/useText";

// Dropdown with rich options (icon, label, description).
const RichSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  hint,
  renderValue,
}) => {
  const { text } = useText("crmUi");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useCloseOnEscape(open, () => setOpen(false));
  const current = options.find((o) => o.value === value);
  return (
    <div ref={ref} className="relative flex flex-col gap-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <button
        id={id}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 rounded-lg border bg-white p-3 text-left text-sm font-medium ${
          open ? "border-primary" : "border-lineStrong"
        }`}
      >
        {current?.icon && (
          <current.icon size={18} className={current.iconClass ?? "text-ink"} />
        )}
        <span
          className={`min-w-0 flex-1 truncate ${
            current ? "text-ink" : "text-faint"
          }`}
        >
          {current
            ? renderValue
              ? renderValue(current)
              : current.label
            : placeholder ?? text("choose")}
        </span>
        <ChevronDownIcon
          size={18}
          className={`text-faint transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {hint && <p className="-mt-1 text-xs text-faint">{hint}</p>}
      {open && (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-xl border border-line bg-white p-1.5 shadow-pop"
          style={label ? { top: 76 } : undefined}
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-surfaceMuted ${
                o.value === value ? "bg-gray-50" : ""
              }`}
            >
              {o.icon && (
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-line bg-white">
                  <o.icon size={18} className={o.iconClass ?? "text-ink"} />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">
                  {o.label}
                </span>
                {o.description && (
                  <span className="block truncate text-xs text-muted">
                    {o.description}
                  </span>
                )}
              </span>
              {o.value === value && (
                <Check size={18} className="text-primary" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RichSelect;
